import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FALLBACK_IMG = "https://images.unsplash.com/photo-1551632811-561732d1e306?auto=format&fit=crop&w=1600&q=80";

export function storyToArticle(s) {
  const paras = s.story.split(/\n+/).map((p) => p.trim()).filter(Boolean);
  const words = s.story.split(/\s+/).length;
  return {
    slug: `story-${s.id}`,
    title: s.title,
    category: s.category,
    read: `${Math.max(2, Math.round(words / 200))} min`,
    date: s.published_at
      ? new Date(s.published_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })
      : "",
    img: s.has_image ? `${API}/stories/${s.id}/image` : FALLBACK_IMG,
    excerpt: paras[0] ? paras[0].slice(0, 140) + (paras[0].length > 140 ? "…" : "") : "",
    body: paras,
    author: s.name,
  };
}

export async function fetchPublishedStories() {
  const r = await axios.get(`${API}/stories/published`);
  return r.data.map(storyToArticle);
}

export const CATEGORIES = ["All", "Training", "Nutrition", "Recovery", "Gear", "Expeditions", "Athlete Stories"];

export const ARTICLES = [
  {
    slug: "eighty-twenty-rule",
    title: "The 80/20 Rule: Why Easy Runs Build Fast Runners",
    category: "Training",
    read: "4 min",
    date: "8 Sep 2026",
    img: "https://images.unsplash.com/photo-1476480862126-209bfaa8edc8?auto=format&fit=crop&w=1600&q=80",
    excerpt: "Most runners train too hard on easy days and too easy on hard days. Here's the fix.",
    body: [
      "Ask any elite coach the secret to improvement and you'll hear the same answer: polarisation. Roughly 80% of your weekly kilometres should feel almost embarrassingly easy — conversational pace, relaxed shoulders, ego parked at the door.",
      "The physiology is simple. Easy running builds mitochondrial density, capillary networks and fat-burning efficiency — the aerobic engine everything else sits on. Running those miles too fast trades adaptation for fatigue.",
      "The hard 20% is where races are sharpened: intervals, tempo runs and hill repetitions. Because your easy days are truly easy, you arrive at quality sessions fresh enough to actually hit them.",
      "Practical test: if you can't speak in full sentences, you're not going easy enough. If your easy pace hasn't changed in months while your mileage crept up, congratulations — the engine is growing.",
      "Try it for eight weeks. Most runners come back confused, then converted — usually with a personal best in hand.",
    ],
  },
  {
    slug: "fueling-the-long-run",
    title: "Fueling the Long Run: Before, During and After",
    category: "Nutrition",
    read: "5 min",
    date: "1 Sep 2026",
    img: "https://images.unsplash.com/photo-1490645935967-10de6ba17061?auto=format&fit=crop&w=1600&q=80",
    excerpt: "Bonking isn't bad luck — it's bad planning. A simple fueling framework for 90+ minute runs.",
    body: [
      "The wall isn't mystical. Your body stores roughly 90 minutes of glycogen at marathon effort. Runs beyond that demand a plan.",
      "Before: a carbohydrate-forward meal 2–3 hours out — poha, bananas, toast with honey. Keep fibre and fat modest to protect your stomach.",
      "During: from minute 45 onward, take 30–60 grams of carbohydrate per hour — gels, dates, or a sports drink. Practice this in training; race day is never the first experiment.",
      "After: within 30 minutes, combine 20–25g of protein with carbohydrates. A simple rule our athletes use — refuel before you shower.",
      "Hydration follows the same logic: little, often, and early. In Indian conditions, add electrolytes on anything over an hour.",
    ],
  },
  {
    slug: "sleep-performance-enhancer",
    title: "Sleep: The Legal Performance Enhancer",
    category: "Recovery",
    read: "3 min",
    date: "24 Aug 2026",
    img: "https://images.unsplash.com/photo-1541781774459-bb2af2f05b55?auto=format&fit=crop&w=1600&q=80",
    excerpt: "You don't get fitter on runs. You get fitter recovering from them — and sleep does the heavy lifting.",
    body: [
      "Training is the stimulus. Adaptation happens in recovery — and no recovery tool outperforms deep sleep, when growth hormone peaks and tissue repair runs at full speed.",
      "Studies on endurance athletes show that even one week of short sleep measurably degrades time-to-exhaustion, pacing judgment and perceived effort. The reverse is also true: sleep extension studies show faster sprint and endurance markers within days.",
      "Targets for runners in a build: 8–9 hours, a consistent wake time, and a cool, dark room. A 20-minute nap after your hardest session of the week is a legitimate training tool, not laziness.",
      "Watch the usual thieves: late-night screens, evening caffeine after 2 PM, and alcohol — which fragments the exact deep-sleep stages you need.",
      "Track nothing for a month except consistency. Same bedtime, same wake time. Your resting heart rate will tell you the rest.",
    ],
  },
  {
    slug: "first-carbon-racer",
    title: "How to Choose Your First Carbon-Plated Racer",
    category: "Gear",
    read: "4 min",
    date: "17 Aug 2026",
    img: "https://images.puma.com/image/upload/f_auto,q_auto,w_1600/global/310474/01/sv01/fnd/AUS/fmt/png/Deviate-NITRO%E2%84%A2-3-Running-Shoes-Men.png",
    excerpt: "Plates aren't magic — they're a tool. When you're ready, and which ones earn the price tag.",
    body: [
      "Carbon plates deliver their famous 4% economy gain — but mostly at paces below roughly 5:00–5:30/km, where foam compression and leg stiffness make the plate work. Slower than that, a great trainer serves you better.",
      "Readiness checklist: you run 40+ km weeks, you've raced at least a 10K recently, and your calves and feet are conditioned — plated shoes shift load differently and demand a few weeks of adaptation.",
      "Our shortlist for Indian runners: the Adidas Adizero EVO SL for value, the Puma Deviate Nitro 3 for aggressive propulsion, and super-shoes from Nike or Asics when you're chasing a major PB.",
      "Fit beats hype. Thumb-width of toe room, locked heel, and no lace pressure over the midfoot. Buy from a store with a runner's return policy.",
      "Rotate them: plate for workouts and races, a daily trainer for everything else. Your legs — and wallet — will last longer.",
    ],
  },
  {
    slug: "marathon-cation-anatomy",
    title: "What a Marathon-Cation Actually Looks Like",
    category: "Expeditions",
    read: "4 min",
    date: "10 Aug 2026",
    img: "https://images.unsplash.com/photo-1461896836934-ffe607ba8211?auto=format&fit=crop&w=1600&q=80",
    excerpt: "Bib, bed, transfers, tribe — a day-by-day anatomy of a Fitygrity race trip.",
    body: [
      "Day minus three: land, settle into a hotel near the start line, light shakeout jog to flush the flight. Dinner with the group — half of racing abroad is who you carb-load with.",
      "Day minus two: expo morning for bibs and gear, then nothing on the feet. City walk optional, film night mandatory.",
      "Race eve: early pasta dinner, gear laid flat, two alarms, phone on charge. We handle logistics; you handle rest.",
      "Race day: coaches at the start pen, crew at two course points, and someone holding your warm layer at the finish. This is why people travel with a tribe.",
      "Day after: slow recovery walk, a long brunch, and the medals photo you'll frame. Then the city is finally yours to explore — legs permitting.",
    ],
  },
  {
    slug: "couch-to-comrades",
    title: "From Couch to Comrades: A Fitygrity Journey",
    category: "Athlete Stories",
    read: "5 min",
    date: "2 Aug 2026",
    img: "https://images.unsplash.com/photo-1551632811-561732d1e306?auto=format&fit=crop&w=1600&q=80",
    excerpt: "Three years ago Meera couldn't run a kilometre. In June she finished the world's oldest ultra.",
    body: [
      "Meera's first run ended at 400 metres, bent double at a Delhi park gate. She was 41, two kids, and a decade removed from any sport.",
      "The plan was deliberately boring: walk-run three days a week, no pace targets, one non-negotiable — never miss twice. Community runs gave her Saturdays a shape.",
      "Year one brought a 10K. Year two, a humid, harrowing first half marathon she calls 'the best bad idea of my life'. The virtual leaderboards kept her honest through the winters.",
      "Comrades entered as a joke over post-run chai. Eighteen months of structured training later — strength work, back-to-back weekends, one carefully managed IT-band scare — she toed the line in Durban.",
      "Her finish-line interview was one sentence: 'I just kept showing up.' That's the whole secret, and it's available to everyone reading this.",
    ],
  },
];
